import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyFeedback } from '../hooks/useFeedbackInstanceApi';
import { useFeedback } from '../context/FeedbackContext';
import {
  FileText,
  CheckCircle,
  Clock,
  TrendingUp,
  Calendar,
  User,
  Users,
  AlertCircle,
  AlertTriangle,
  Search,
  ChevronDown,
  Eye,
  Inbox,
} from 'lucide-react';

type TabType = 'pending' | 'completed' | 'all';
type SortType = 'deadline' | 'assigned' | 'title';

interface FeedbackOccurrenceItem {
  id: string;
  feedbackInstanceId: string;
  feedbackId: string;
  feedbackTitle?: string;
  assignedTo: string[];
  assigneeType: 'user' | 'group' | 'role';
  frequency: 'once' | 'daily' | 'weekly' | 'monthly';
  occurrenceNumber: number;
  startsAt: Date;
  endsAt?: Date;
  status: string;
  hasUserResponded: boolean;
  periodLabel: string;
}

const formatPeriodLabel = (frequency: string, startsAt: Date): string => {
  if (frequency === 'monthly') {
    return startsAt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  if (frequency === 'weekly') {
    return `Week of ${startsAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  }
  if (frequency === 'daily') {
    return startsAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  return '';
};

const getDeadlineStatus = (endDate?: Date) => {
  if (!endDate) return { label: 'No deadline', color: 'gray', icon: Calendar, urgency: 0 };

  const now = new Date();
  const diffTime = endDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) return { label: 'Overdue', color: 'red', icon: AlertCircle, urgency: 3 };
  if (daysRemaining === 0) return { label: 'Due today', color: 'red', icon: AlertTriangle, urgency: 2 };
  if (daysRemaining <= 2) return { label: `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`, color: 'red', icon: AlertTriangle, urgency: 2 };
  if (daysRemaining <= 7) return { label: `${daysRemaining} days left`, color: 'orange', icon: Clock, urgency: 1 };
  return { label: `${daysRemaining} days left`, color: 'green', icon: Calendar, urgency: 0 };
};

const DeadlineBadge: React.FC<{ endDate?: Date }> = ({ endDate }) => {
  const status = getDeadlineStatus(endDate);
  const Icon = status.icon;

  const colorClasses = {
    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[status.color as keyof typeof colorClasses]}`}>
      <Icon className="h-3 w-3 mr-1" />
      {status.label}
    </span>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  badge?: { label: string; color: string };
}> = ({ label, value, icon: Icon, color, badge }) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500',
    orange: 'bg-orange-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
            {badge && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                badge.color === 'red' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : ''
              }`}>
                {badge.label}
              </span>
            )}
          </div>
        </div>
        <div className={`${colorClasses[color]} p-3 rounded-full`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ type: TabType }> = ({ type }) => {
  const config = {
    pending: {
      icon: CheckCircle,
      title: 'All caught up!',
      description: "You don't have any pending feedback to complete. Check back later for new assignments.",
    },
    completed: {
      icon: Inbox,
      title: 'No completed feedback yet',
      description: "Once you complete feedback forms, they'll appear here for your review.",
    },
    all: {
      icon: FileText,
      title: 'No feedback assigned',
      description: "You haven't been assigned any feedback forms yet.",
    },
  };

  const { icon: Icon, title, description } = config[type];

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-6 mb-4">
        <Icon className="h-12 w-12 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">{description}</p>
    </div>
  );
};

const MyFeedback: React.FC = () => {
  const navigate = useNavigate();
  const { data: myFeedbackData, isLoading } = useMyFeedback();
  const { feedbackList } = useFeedback();

  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortType>('deadline');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const feedbackInstances: FeedbackOccurrenceItem[] = useMemo(() => {
    if (!myFeedbackData || !Array.isArray(myFeedbackData)) return [];

    return myFeedbackData.map(item => {
      const startsAt = new Date(item.startsAt);
      return {
        id: item.id,
        feedbackInstanceId: item.feedbackInstanceId,
        feedbackId: item.feedbackId,
        feedbackTitle: item.feedbackTitle,
        assignedTo: Array.isArray(item.assignedTo) ? item.assignedTo : [],
        assigneeType: item.assigneeType,
        frequency: item.frequency,
        occurrenceNumber: item.occurrenceNumber,
        startsAt,
        endsAt: item.endsAt ? new Date(item.endsAt) : undefined,
        status: item.status,
        hasUserResponded: item.hasUserResponded ?? false,
        periodLabel: formatPeriodLabel(item.frequency, startsAt),
      };
    });
  }, [myFeedbackData]);

  const { pendingCount, completedCount, urgentCount, responseRate } = useMemo(() => {
    const pending = feedbackInstances.filter(i => !i.hasUserResponded);
    const completed = feedbackInstances.filter(i => i.hasUserResponded);
    const urgent = pending.filter(i => {
      const status = getDeadlineStatus(i.endsAt);
      return status.urgency >= 2;
    });
    const rate = feedbackInstances.length > 0
      ? Math.round((completed.length / feedbackInstances.length) * 100)
      : 0;

    return {
      pendingCount: pending.length,
      completedCount: completed.length,
      urgentCount: urgent.length,
      responseRate: rate,
    };
  }, [feedbackInstances]);

  const filteredInstances = useMemo(() => {
    let filtered = feedbackInstances;

    if (activeTab === 'pending') {
      filtered = filtered.filter(i => !i.hasUserResponded);
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(i => i.hasUserResponded);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(instance => {
        const title = instance.feedbackTitle || feedbackList.find(s => s.id === instance.feedbackId)?.title;
        return title?.toLowerCase().includes(term);
      });
    }

    filtered.sort((a, b) => {
      if (sortBy === 'deadline') {
        const aStatus = getDeadlineStatus(a.endsAt);
        const bStatus = getDeadlineStatus(b.endsAt);
        if (bStatus.urgency !== aStatus.urgency) return bStatus.urgency - aStatus.urgency;
        if (!a.endsAt) return 1;
        if (!b.endsAt) return -1;
        return a.endsAt.getTime() - b.endsAt.getTime();
      } else if (sortBy === 'assigned') {
        return b.startsAt.getTime() - a.startsAt.getTime();
      } else {
        const titleA = a.feedbackTitle || feedbackList.find(s => s.id === a.feedbackId)?.title || '';
        const titleB = b.feedbackTitle || feedbackList.find(s => s.id === b.feedbackId)?.title || '';
        return titleA.localeCompare(titleB);
      }
    });

    return filtered;
  }, [feedbackInstances, activeTab, searchTerm, sortBy, feedbackList]);

  const handleViewResponse = (feedbackId: string, instanceId: string) => {
    navigate(`/feedback/response/${feedbackId}/${instanceId}`);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            ))}
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Feedback</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Assigned"
          value={feedbackInstances.length}
          icon={FileText}
          color="blue"
        />
        <StatCard
          label="Pending"
          value={pendingCount}
          icon={Clock}
          color="orange"
          badge={urgentCount > 0 ? { label: `${urgentCount} urgent`, color: 'red' } : undefined}
        />
        <StatCard
          label="Completed"
          value={completedCount}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          label="Response Rate"
          value={`${responseRate}%`}
          icon={TrendingUp}
          color="purple"
        />
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          {(['pending', 'completed', 'all'] as TabType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700">
                {tab === 'pending' ? pendingCount : tab === 'completed' ? completedCount : feedbackInstances.length}
              </span>
            </button>
          ))}
        </nav>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search feedback..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:text-white"
          >
            Sort: {sortBy === 'deadline' ? 'Deadline' : sortBy === 'assigned' ? 'Date Assigned' : 'Title'}
            <ChevronDown className="h-4 w-4" />
          </button>

          {showSortDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowSortDropdown(false)} />
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-20">
                {[
                  { value: 'deadline', label: 'Deadline' },
                  { value: 'assigned', label: 'Date Assigned' },
                  { value: 'title', label: 'Title' },
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value as SortType);
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      sortBy === option.value ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {filteredInstances.length === 0 ? (
        <EmptyState type={activeTab} />
      ) : (
        <div className="space-y-4">
          {filteredInstances.map(instance => {
            const feedback = feedbackList.find(s => s.id === instance.feedbackId);
            const deadlineStatus = getDeadlineStatus(instance.endsAt);
            const isPending = !instance.hasUserResponded;

            return (
              <div
                key={instance.id}
                className={`relative rounded-lg border transition-all ${
                  isPending
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                }`}
              >
                {isPending && deadlineStatus.urgency >= 2 && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg" />
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {instance.feedbackTitle || feedback?.title || 'Untitled Feedback'}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {isPending && <DeadlineBadge endDate={instance.endsAt} />}

                        {instance.assigneeType === 'user' ? (
                          <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full text-xs font-medium">
                            <User className="h-3 w-3 mr-1" />
                            Direct
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 rounded-full text-xs font-medium">
                            <Users className="h-3 w-3 mr-1" />
                            Team
                          </span>
                        )}

                        {instance.frequency !== 'once' && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium capitalize">
                            {instance.frequency}
                          </span>
                        )}
                        {instance.periodLabel && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-400 rounded-full text-xs font-medium">
                            {instance.periodLabel}
                          </span>
                        )}
                      </div>
                    </div>

                    {!isPending && (
                      <span className="ml-3 inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full text-xs font-medium">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Completed
                      </span>
                    )}
                  </div>

                  {feedback?.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {feedback.description}
                    </p>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <span className="flex items-center">
                      <Calendar className="h-3.5 w-3.5 mr-1" />
                      Assigned {instance.startsAt.toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex justify-end">
                    {isPending ? (
                      <button
                        onClick={() => navigate(`/feedback/take/${instance.feedbackId}/${instance.feedbackInstanceId}?occurrenceId=${instance.id}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Provide Feedback
                      </button>
                    ) : (
                      <button
                        onClick={() => handleViewResponse(instance.feedbackId, instance.feedbackInstanceId)}
                        className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Response
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyFeedback;
